import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import FileUpload from '../../../components/ui/FileUpload';

const ProjectModal = ({ isOpen, onClose, onSave, project, floorTypes, rooms }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    floorType: '',
    room: '',
    tags: '',
    isFeatured: false,
    images: []
  });

  useEffect(() => {
    if (project) {
      setFormData({
        title: project?.title || '',
        description: project?.description || '',
        floorType: project?.floorType || '',
        room: project?.room || '',
        tags: project?.tags?.join(', ') || '',
        isFeatured: project?.isFeatured || false,
        images: project?.images || []
      });
    } else {
      setFormData({
        title: '',
        description: '',
        floorType: '',
        room: '',
        tags: '',
        isFeatured: false,
        images: []
      });
    }
  }, [project]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    const tagsArray = formData?.tags?.split(',')?.map(tag => tag?.trim())?.filter(tag => tag);
    onSave({
      ...project,
      ...formData,
      tags: tagsArray
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[1000] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-text-primary">
            {project ? 'Edit Project' : 'Add New Project'}
          </h2>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors duration-150"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <Input
            label="Project Title"
            type="text"
            placeholder="Enter project title"
            value={formData?.title}
            onChange={(e) => handleChange('title', e?.target?.value)}
            required
          />

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Description
            </label>
            <textarea
              className="w-full px-3 py-2 bg-background border border-input rounded-lg text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              rows={4}
              placeholder="Enter project description"
              value={formData?.description}
              onChange={(e) => handleChange('description', e?.target?.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Floor Type"
              options={floorTypes?.map(ft => ({ value: ft, label: ft }))}
              value={formData?.floorType}
              onChange={(value) => handleChange('floorType', value)}
              placeholder="Select floor type"
              required
            />

            <Select
              label="Room"
              options={rooms?.map(r => ({ value: r, label: r }))}
              value={formData?.room}
              onChange={(value) => handleChange('room', value)}
              placeholder="Select room"
              required
            />
          </div>

          <Input
            label="Tags"
            type="text"
            placeholder="Enter tags separated by commas"
            description="e.g., Modern, Residential, High-gloss"
            value={formData?.tags}
            onChange={(e) => handleChange('tags', e?.target?.value)}
          />

          <FileUpload
            label="Project Images"
            description="Upload project images (max 5MB each)"
            accept="image/*"
            multiple
            maxSize={5242880}
            onUpload={(file) => {
              console.log('File uploaded:', file?.name);
            }}
            error=""
          />

          <Checkbox
            label="Featured Project"
            description="Featured projects appear prominently in customer galleries"
            checked={formData?.isFeatured}
            onChange={(e) => handleChange('isFeatured', e?.target?.checked)}
          />

          <div className="flex items-center gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              className="flex-1"
              iconName="Save"
              iconPosition="left"
              iconSize={16}
            >
              {project ? 'Update Project' : 'Add Project'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectModal;